import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
	type CreateProjectBody,
	PROJECT_LIMITS,
	PROJECT_TYPES,
	type ProjectResponse,
	type ProjectType,
	type PropertyDTO,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { useJournalCacheInvalidator } from "../hooks/use-journal-cache-invalidator"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

function fillTemplate(
	template: string,
	vars: Record<string, string | number>,
): string {
	return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ""))
}

export function CreateProjectModal({
	property,
	onClose,
}: {
	property: PropertyDTO
	onClose: () => void
}) {
	const navigate = useNavigate()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const queryClient = useQueryClient()
	const invalidateJournal = useJournalCacheInvalidator(property.id)

	// `construction` is only creatable for houses — the API enforces it;
	// the select simply never offers it elsewhere (spec ZPL-97).
	const availableTypes = PROJECT_TYPES.filter(
		(pt) => pt !== "construction" || property.type === "house",
	)

	const [name, setName] = useState("")
	const [type, setType] = useState<ProjectType | "">("")
	const [description, setDescription] = useState("")
	const [errors, setErrors] = useState<{
		name?: string
		type?: string
		description?: string
		form?: string
	}>({})

	const createProject = useMutation({
		mutationFn: (body: CreateProjectBody) =>
			apiFetch<ProjectResponse>(
				`/api/common/properties/${property.id}/projects`,
				{ method: "POST", body: JSON.stringify(body) },
			),
		onSuccess: async (res) => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.projects(property.id),
			})
			invalidateJournal()
			// No onClose() here — navigation unmounts the list page (and this
			// modal with it); closing first would set state on a component
			// that's about to unmount (peer-review finding).
			navigate(webPaths.project(property.id, res.project.id))
		},
	})

	// Match share-modal.tsx's close idiom: Escape dismisses the dialog.
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	function validate(): boolean {
		const next: typeof errors = {}
		const trimmedName = name.trim()
		if (trimmedName.length === 0) {
			next.name = t.projects.form.errors.nameRequired
		} else if (trimmedName.length > PROJECT_LIMITS.nameMax) {
			next.name = fillTemplate(t.projects.form.errors.nameTooLong, {
				max: PROJECT_LIMITS.nameMax,
			})
		}
		if (!type) next.type = t.projects.form.errors.typeRequired
		if (description.trim().length > PROJECT_LIMITS.descriptionMax) {
			next.description = fillTemplate(
				t.projects.form.errors.descriptionTooLong,
				{ max: PROJECT_LIMITS.descriptionMax },
			)
		}
		setErrors(next)
		return Object.keys(next).length === 0
	}

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault()
		if (!validate()) return
		setErrors((prev) => ({ ...prev, form: undefined }))
		const trimmedDescription = description.trim()
		const body: CreateProjectBody = {
			name: name.trim(),
			type: type as ProjectType,
			...(trimmedDescription.length > 0
				? { description: trimmedDescription }
				: {}),
		}
		try {
			await createProject.mutateAsync(body)
		} catch (err) {
			const message =
				err instanceof ApiError ? err.message : t.projects.form.errors.unknown
			setErrors((prev) => ({ ...prev, form: message }))
		}
	}

	return (
		// Overlay + dialog wrapper mirrors share-modal.tsx: role/aria-modal on
		// the overlay, a full-bleed backdrop button for click-to-dismiss, an
		// Escape handler (above), and a × close button in the header.
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="create-project-title"
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={t.projects.form.cancel}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-md rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-2xl">
				<div className="flex items-start justify-between">
					<h2
						id="create-project-title"
						className="text-xl font-bold text-foreground"
					>
						{t.projects.form.createTitle}
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground px-2 py-1"
						aria-label={t.projects.form.cancel}
					>
						×
					</button>
				</div>
				<form onSubmit={onSubmit} className="mt-5 space-y-4" noValidate>
					<div>
						<label
							htmlFor="project-name"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.projects.form.nameLabel}
						</label>
						<input
							id="project-name"
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t.projects.form.namePlaceholder}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							maxLength={PROJECT_LIMITS.nameMax + 10}
							aria-invalid={Boolean(errors.name)}
						/>
						{errors.name ? (
							<p className="text-sm text-destructive mt-1">{errors.name}</p>
						) : null}
					</div>

					<fieldset>
						<legend className="block text-sm font-semibold text-foreground/80 mb-2">
							{t.projects.form.typeLabel}
						</legend>
						<div className="grid grid-cols-3 gap-2">
							{availableTypes.map((pt) => (
								<label
									key={pt}
									className={
										"flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm " +
										(type === pt
											? "border-ring bg-accent text-foreground"
											: "border-border bg-muted/40 text-foreground/80 hover:border-foreground/25")
									}
								>
									<input
										type="radio"
										name="project-type"
										value={pt}
										checked={type === pt}
										onChange={() => setType(pt)}
										className="sr-only"
									/>
									<span>{t.projects.types[pt]}</span>
								</label>
							))}
						</div>
						{errors.type ? (
							<p className="text-sm text-destructive mt-2">{errors.type}</p>
						) : null}
					</fieldset>

					<div>
						<label
							htmlFor="project-description"
							className="block text-sm font-semibold text-foreground/80 mb-2"
						>
							{t.projects.form.descriptionLabel}
						</label>
						<textarea
							id="project-description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder={t.projects.form.descriptionPlaceholder}
							rows={3}
							className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none resize-y"
							maxLength={PROJECT_LIMITS.descriptionMax + 100}
							aria-invalid={Boolean(errors.description)}
						/>
						{errors.description ? (
							<p className="text-sm text-destructive mt-1">
								{errors.description}
							</p>
						) : null}
					</div>

					{errors.form ? (
						<div
							role="alert"
							className="rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
						>
							{errors.form}
						</div>
					) : null}

					<div className="flex items-center justify-end gap-3 pt-2">
						<Button
							type="button"
							variant="ghost"
							onClick={onClose}
							disabled={createProject.isPending}
						>
							{t.projects.form.cancel}
						</Button>
						<Button
							type="submit"
							variant="brand"
							disabled={createProject.isPending}
						>
							{t.projects.form.submit}
						</Button>
					</div>
				</form>
			</div>
		</div>
	)
}
